import React from 'react'
import styled from '@emotion/styled'
import Carousel, { CSSProperties } from 'nuka-carousel'
import { IconButton } from '@mui/material'
import { NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { useRoles, checkRoles } from '@edge/spark/io-roles'
import { CardViewMode } from '@edge/spark/io-dashboards'
import { useBreakpoint, useIsLandscape } from '@edge/spark/ui-mui'
import { widgets as widgetList } from '../../../feature-dashboard-widgets'
import type { DashboardLayout, DashboardCardDef } from '@edge/spark/io-dashboards'
import { CardDetailModal } from '../CardDetailModal'

const Container = styled.div`
    width: 100%;

    .slider-frame {
        padding-bottom: 14px !important;
    }
`

const slidesToShowMap = new Map(
    Object.entries({
        xs: 1,
        sm: 3,
        md: 3,
        lg: 4,
        xl: 4,
    }),
)
const slidesToShowMapLandscape = new Map(
    Object.entries({
        xs: 2,
        sm: 3,
        md: 3,
        lg: 4,
        xl: 4,
    }),
)

interface Props {
    dashboardLayout: DashboardLayout
}

export const Panel = ({ dashboardLayout }: Props): React.ReactElement => {
    // Pull this from the layout not the source
    const widgets = dashboardLayout.cards
    const roles = useRoles()
    const breakpoint = useBreakpoint()
    const landscape = useIsLandscape()

    const [searchWidget, setSearchWidget] = React.useState<null | DashboardCardDef>(null)

    const showDetails = (widgetId: string): void => {
        // handle widget config modal.
        const widget = widgets.find(({ id }) => id === widgetId)
        setSearchWidget(widget || null)
    }

    const handleCloseConfiguration = (): void => {
        // Handle the resetting the config on the card.
        setSearchWidget(null)
    }

    const slidesToShow = breakpoint
        ? landscape
            ? slidesToShowMapLandscape.get(breakpoint)
            : slidesToShowMap.get(breakpoint)
        : 1

    const getWidget = (c: DashboardCardDef): React.ReactNode => {
        try {
            const foundWidget = widgetList.find(({ name }) => name === c.type)
            const Widget = foundWidget?.component
            if (!Widget) {
                return null
            }
            if (foundWidget?.requiredRoleOption && foundWidget.requiredRoleOption?.length > 0) {
                const { requiredRoleOption } = foundWidget
                if (!roles || roles.length === 0) {
                    return null
                }
                const response = checkRoles(roles, requiredRoleOption)
                if (!response) {
                    return null
                }
            }
            return c.config ? (
                <Widget
                    config={c.config}
                    widgetId={c.id}
                    cardViewMode={CardViewMode.PANEL}
                    handleDisplayDetails={showDetails}
                    readonly={false}
                    key={c.id}
                />
            ) : null
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
            return null
        }
    }

    return (
        <Container>
            <Carousel
                getControlsContainerStyles={(key): CSSProperties => {
                    switch (key) {
                        case 'BottomCenter':
                            return {
                                bottom: -30,
                            }
                        default:
                            return {}
                    }
                }}
                slidesToShow={slidesToShow}
                cellSpacing={10}
                framePadding="0 30px"
                swiping={false}
                // scrollMode="page"
                renderCenterLeftControls={({ previousSlide }): React.ReactNode => (
                    <IconButton color="primary" edge="start" onClick={previousSlide}>
                        <NavigateBeforeIcon />
                    </IconButton>
                )}
                renderCenterRightControls={({ nextSlide }): React.ReactNode => (
                    <IconButton color="primary" edge="end" onClick={nextSlide}>
                        <NavigateNextIcon />
                    </IconButton>
                )}
            >
                {widgets
                    .map((w) => {
                        return getWidget(w)
                    })
                    .filter((element) => element !== null)}
            </Carousel>
            {searchWidget && <CardDetailModal widgetDef={searchWidget} handleClose={handleCloseConfiguration} />}
        </Container>
    )
}
